/** Types and small pieces shared by the integration screens (M15). */
import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { ApiError } from '../../lib/api';
import { Alert, Button, Modal, StatusChip, type ChipTone } from '../../components/ui';

export type RuleStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ACTIVE' | 'RETIRED';

export const RULE_TONE: Record<RuleStatus, ChipTone> = { DRAFT: 'neutral', SUBMITTED: 'warning', APPROVED: 'info', ACTIVE: 'ok', RETIRED: 'neutral' };

export function RuleStatusChip({ status }: { status: RuleStatus }): JSX.Element {
  return <StatusChip tone={RULE_TONE[status]}>{status.charAt(0) + status.slice(1).toLowerCase()}</StatusChip>;
}

export const EVENT_TONE: Record<string, ChipTone> = { RECEIVED: 'info', POSTED: 'ok', EXCEPTION: 'critical', IGNORED: 'neutral' };

/** One message from an API failure, with field errors listed. */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const fields = Object.entries(e.fields);
    return fields.length ? `${e.message} ${fields.map(([f, m]) => `${f}: ${m}`).join(' · ')}` : e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }): JSX.Element {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? <Check aria-hidden="true" className="h-4 w-4" /> : <Copy aria-hidden="true" className="h-4 w-4" />}
      {done ? 'Copied' : label}
    </Button>
  );
}

/** Shows a secret exactly once, with a clear warning. */
export function SecretReveal({ title, items, onClose }: { title: string; items: { label: string; value: string }[]; onClose: () => void }): JSX.Element {
  return (
    <Modal title={title} description="Copy these now. The secret is stored only as a hash and cannot be shown again." onClose={onClose}>
      <div className="space-y-3">
        {items.map((i) => (
          <div key={i.label}>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{i.label}</p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-xs dark:bg-slate-800">{i.value}</code>
              <CopyButton value={i.value} />
            </div>
          </div>
        ))}
        <Alert tone="warning">Store the secret in the calling system’s secret manager, never in source code or chat.</Alert>
        <div className="flex justify-end">
          <Button onClick={onClose}>I have saved it</Button>
        </div>
      </div>
    </Modal>
  );
}

export function JsonBlock({ value }: { value: unknown }): JSX.Element {
  return <pre className="max-h-80 overflow-auto rounded-xl bg-slate-100 p-3 font-mono text-xs text-slate-800 dark:bg-slate-950 dark:text-slate-200">{JSON.stringify(value, null, 2)}</pre>;
}
